import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Grid, List, Download, Upload, 
  FileText, Image, Video, File, 
  MoreVertical, Star, X, Share2
} from 'lucide-react';

// Mock data
const mockFiles = [
  {
    id: 1,
    name: 'Ice_Karkade_Campaign_Final.psd',
    type: 'image',
    category: 'Campaigns',
    size: '24.5 MB',
    uploadedBy: 'Mohammed Ali',
    uploadedAt: '2024-06-01',
    tags: ['campaign', 'summer', 'final'],
    thumbnail: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=120&h=120&fit=crop',
    downloads: 15,
    starred: true
  },
  {
    id: 2,
    name: 'Brand_Guidelines_2024.pdf',
    type: 'document',
    category: 'Brand',
    size: '8.2 MB',
    uploadedBy: 'Sarah Ahmed',
    uploadedAt: '2024-05-28',
    tags: ['brand', 'guidelines', '2024'],
    thumbnail: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=120&h=120&fit=crop',
    downloads: 42,
    starred: false
  },
  {
    id: 3,
    name: 'Product_Photos_Collection.zip',
    type: 'archive',
    category: 'Products',
    size: '156.7 MB',
    uploadedBy: 'Fatima Hassan',
    uploadedAt: '2024-05-25',
    tags: ['products', 'photos', 'collection'],
    thumbnail: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=120&h=120&fit=crop',
    downloads: 8,
    starred: false
  },
  {
    id: 4,
    name: 'Summer_Campaign_Video.mp4',
    type: 'video',
    category: 'Campaigns',
    size: '89.3 MB',
    uploadedBy: 'Ahmed Omar',
    uploadedAt: '2024-05-30',
    tags: ['video', 'campaign', 'summer'],
    thumbnail: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=120&h=120&fit=crop',
    downloads: 23,
    starred: true
  },
  {
    id: 5,
    name: 'Logo_Variations.ai',
    type: 'image',
    category: 'Brand',
    size: '12.8 MB',
    uploadedBy: 'Emma Davis',
    uploadedAt: '2024-05-22',
    tags: ['logo', 'brand', 'variations'],
    thumbnail: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=120&h=120&fit=crop',
    downloads: 31,
    starred: true
  },
  {
    id: 6,
    name: 'Social_Media_Templates.sketch',
    type: 'document',
    category: 'Templates',
    size: '18.4 MB',
    uploadedBy: 'Alex Kim',
    uploadedAt: '2024-05-20',
    tags: ['templates', 'social', 'media'],
    thumbnail: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=120&h=120&fit=crop',
    downloads: 19,
    starred: false
  }
];

const categories = [
  { id: 'all', name: 'All Files', count: mockFiles.length, color: 'bg-gray-500' },
  { id: 'campaigns', name: 'Campaigns', count: 2, color: 'bg-blue-500' },
  { id: 'brand', name: 'Brand', count: 2, color: 'bg-purple-500' },
  { id: 'products', name: 'Products', count: 1, color: 'bg-green-500' },
  { id: 'templates', name: 'Templates', count: 1, color: 'bg-orange-500' },
  { id: 'resources', name: 'Resources', count: 0, color: 'bg-pink-500' }
];

const FileLibrary = ({ isDarkMode = true }) => {
  const [files, setFiles] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    setFiles(mockFiles);
  }, []);

  const getFileIcon = (type) => {
    switch (type) {
      case 'image': return <Image className="h-8 w-8 text-blue-500" />;
      case 'video': return <Video className="h-8 w-8 text-red-500" />;
      case 'document': return <FileText className="h-8 w-8 text-green-500" />;
      case 'archive': return <File className="h-8 w-8 text-orange-500" />;
      default: return <File className="h-8 w-8 text-gray-500" />;
    }
  };

  const filteredFiles = files.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         file.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || file.category.toLowerCase() === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const FileCard = ({ file }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-xl rounded-2xl p-4 shadow-lg border transition-all duration-300 cursor-pointer group ${
        isDarkMode 
          ? 'bg-black/20 border-white/10 hover:shadow-xl hover:bg-black/30' 
          : 'bg-white/80 border-white/20 hover:shadow-xl hover:bg-white/90'
      }`}
      onClick={() => setSelectedFile(file)}
    >
      <div className="relative">
        <div className={`aspect-square rounded-xl mb-3 flex items-center justify-center overflow-hidden ${
          isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
        }`}>
          {file.type === 'image' ? (
            <img 
              src={file.thumbnail} 
              alt={file.name}
              className="w-full h-full object-cover"
            />
          ) : (
            getFileIcon(file.type)
          )}
        </div>
        
        <div className="absolute top-2 right-2 flex space-x-1">
          {file.starred && (
            <div className="p-1 bg-yellow-100 rounded-full">
              <Star className="h-4 w-4 text-yellow-600 fill-current" />
            </div>
          )}
          <div className={`p-1 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${
            isDarkMode ? 'bg-black/80' : 'bg-white/80'
          }`}>
            <MoreVertical className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </div>
        </div>
      </div>

      <div>
        <h3 className={`font-semibold text-sm mb-1 truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{file.name}</h3>
        <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{file.size}</p>
        
        <div className={`flex items-center justify-between text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          <span>{file.uploadedBy}</span>
          <span>{file.downloads} downloads</span>
        </div>

        <div className="flex flex-wrap gap-1 mt-2">
          {file.tags.slice(0, 2).map(tag => (
            <span key={tag} className={`px-2 py-1 text-xs rounded-full ${
              isDarkMode 
                ? 'bg-purple-500/20 text-purple-300' 
                : 'bg-purple-100 text-purple-700'
            }`}>
              {tag}
            </span>
          ))}
          {file.tags.length > 2 && (
            <span className={`px-2 py-1 text-xs rounded-full ${
              isDarkMode 
                ? 'bg-gray-500/20 text-gray-400' 
                : 'bg-gray-100 text-gray-700'
            }`}>
              +{file.tags.length - 2}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );

  const FileListItem = ({ file }) => (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className={`flex items-center space-x-4 p-4 backdrop-blur-xl rounded-xl shadow-sm border transition-all duration-300 cursor-pointer ${
        isDarkMode 
          ? 'bg-black/20 border-white/10 hover:shadow-md hover:bg-black/30' 
          : 'bg-white/80 border-white/20 hover:shadow-md hover:bg-white/90'
      }`}
      onClick={() => setSelectedFile(file)}
    >
      <div className="flex items-center space-x-3 flex-1">
        {getFileIcon(file.type)}
        <div className="flex-1">
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{file.name}</h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{file.category} • {file.size}</p>
        </div>
      </div>

      <div className={`flex items-center space-x-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
        <span>{file.uploadedBy}</span>
        <span>{file.downloads} downloads</span>
        <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
        {file.starred && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
      </div>

      <div className="flex items-center space-x-2">
        <button className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors">
          <Download className="h-4 w-4" />
        </button>
        <button className="p-2 text-gray-400 hover:text-green-500 hover:bg-green-50 rounded-lg transition-colors">
          <Share2 className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );

  const FileDetailModal = ({ file, isOpen, onClose }) => {
    if (!file) return null;

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
                isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-gray-200'
              }`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{file.name}</h2>
                  <button
                    onClick={onClose}
                    className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                    }`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* File Preview */}
                  <div>
                    <div className={`aspect-square rounded-xl flex items-center justify-center overflow-hidden ${
                      isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
                    }`}>
                      {file.type === 'image' ? (
                        <img 
                          src={file.thumbnail} 
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-center">
                          {getFileIcon(file.type)}
                          <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{file.type.toUpperCase()}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* File Details */}
                  <div className="space-y-4">
                    <div>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>File Information</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Size</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{file.size}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Category</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{file.category}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Uploaded by</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{file.uploadedBy}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Upload date</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{new Date(file.uploadedAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Downloads</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{file.downloads}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {file.tags.map(tag => (
                          <span key={tag} className={`px-3 py-1 text-sm rounded-full ${
                            isDarkMode 
                              ? 'bg-purple-500/20 text-purple-300' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2">
                        <Download className="h-4 w-4" />
                        <span>Download</span>
                      </button>
                      <button className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2">
                        <Share2 className="h-4 w-4" />
                        <span>Share</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <div className="w-full h-full px-8 py-8 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0"
      >
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
            File Library
          </h1>
          <p className="text-gray-300 mt-2 text-lg">
            Manage and organize your creative assets and resources
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
          onClick={() => {
            // TODO: Implement upload functionality
          }}
        >
          <Upload className="h-5 w-5" />
          <span>Upload Files</span>
        </motion.button>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`backdrop-blur-xl rounded-2xl p-6 shadow-lg border ${
          isDarkMode 
            ? 'bg-black/20 border-white/10' 
            : 'bg-white/80 border-gray-200/50'
        }`}
      >
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 ${
                  isDarkMode 
                    ? 'bg-black/20 border border-white/10 text-white placeholder-gray-400' 
                    : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Category Filter */}
            <div className="flex space-x-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-white hover:bg-white/10'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>

            {/* View Mode Toggle */}
            <div className={`flex rounded-lg p-1 ${
              isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
            }`}>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-all duration-200 ${
                  viewMode === 'grid'
                    ? isDarkMode 
                      ? 'bg-white/20 text-white' 
                      : 'bg-white text-purple-600 shadow-sm'
                    : isDarkMode
                      ? 'text-gray-400 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-all duration-200 ${
                  viewMode === 'list'
                    ? isDarkMode 
                      ? 'bg-white/20 text-white' 
                      : 'bg-white text-purple-600 shadow-sm'
                    : isDarkMode
                      ? 'text-gray-400 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Files Grid/List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className={
          viewMode === 'grid'
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            : 'space-y-4'
        }
      >
        {filteredFiles.map((file, index) => (
          <motion.div
            key={file.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.1 }}
          >
            {viewMode === 'grid' ? (
              <FileCard file={file} />
            ) : (
              <FileListItem file={file} />
            )}
          </motion.div>
        ))}
      </motion.div>

      {/* File Detail Modal */}
      <FileDetailModal 
        file={selectedFile}
        isOpen={!!selectedFile}
        onClose={() => setSelectedFile(null)}
      />
    </div>
  );
};

export default FileLibrary; 